/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/utils/roleDashboardMap.ts
 * PURPOSE: Single source for TenantUserRole -> post-login dashboard route
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (Code Reusability)
 * SPRINT GOVERNANCE: Rule 2: Milestone-Gated Roadmaps
 *
 * @business-context
 * - Domain: Tenant user routing (SC-001-LOGIN), Vietnamese F&B Multi-Tenant Platform
 * - Impact: Any drift between this map and the backend equivalent sends a role to
 *   the wrong dashboard after login
 * - Stakeholders: All tenant users (routed on every login), Frontend team
 *
 * @technical-architecture
 * - Layer: Utility Layer
 * - Pattern: Pure lookup function — single source, no side effects
 * - Dependencies: none
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/persona-flows/WIREFRAME-V0-SCREEN-SPEC-MAP.md (SC-001-LOGIN §C — role -> home_route)
 * - Backend mirror (MUST stay in sync): backend/app/models/tenant_user.py TenantUserRole.get_home_route()
 * - Consumers: frontend/src/pages/Login.tsx (login-time navigation), frontend/src/App.tsx DashboardGuard (direct /dashboard hits)
 *
 * Consolidation note: previously duplicated as two independent maps
 * (Login.tsx `ROLE_DASHBOARD_MAP` + App.tsx `DashboardGuard` inline if/else).
 * As of the backend `home_route` field (login response), this map is only the
 * FALLBACK path — used when a server-provided home_route is not available
 * (e.g. a session persisted before this field existed).
 */

/** Role -> dashboard path. Mirrors backend TenantUserRole.get_home_route() verbatim. */
export const ROLE_DASHBOARD_MAP: Record<string, string> = {
  TENANT_ADMIN: "/tenant-admin/dashboard",
  TENANT_OWNER: "/dashboard/chairman",
  CORPORATE_MANAGER: "/manager/hub",
  BRANCH_MANAGER: "/manager/hub",
  SHIFT_MANAGER: "/manager/hub",
  HEAD_CHEF: "/manager/hub",
  CHEF: "/dashboard/staff",
  WAITER: "/dashboard/staff",
  CASHIER: "/dashboard/staff",
  KITCHEN_STAFF: "/dashboard/staff",
  DELIVERY_STAFF: "/dashboard/staff",
  CLEANER: "/dashboard/staff",
  TRAINEE: "/dashboard/staff",
};

/** Default landing route when role is unmapped/unknown. */
export const DEFAULT_DASHBOARD_ROUTE = "/dashboard/staff";

/**
 * Resolve a TenantUserRole string to its dashboard route.
 * Case-insensitive (callers have historically passed both raw enum values and
 * `.toUpperCase()`'d strings) — falls back to DEFAULT_DASHBOARD_ROUTE.
 */
export function getDashboardRouteForRole(role: string | null | undefined): string {
  if (!role) return DEFAULT_DASHBOARD_ROUTE;
  return ROLE_DASHBOARD_MAP[role.toUpperCase()] || DEFAULT_DASHBOARD_ROUTE;
}
